import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Hubspot from '../pages/Extras/Hubspot';
import SocialsPage from '../pages/Socials/socials';
import createStore from 'polotno/model/store';
import { getResetStoreCounter, getSessionsLoading } from '../store/selector/common.selector';
import { useAppDispatch, useAppSelector } from '../store/store';
import { useLazyGetBrandElementsQuery } from '../store/api/brandMagic.api';
import {
    BrandElementsFontsClass,
    IDesignElementsFonts,
    IGetBrandElements,
} from '../interfaces/brand-magic.interface';
import { getGoogleFontsListAPI, setColorsPresetFunc, setGoogleFonts } from 'polotno/config';
import { mapFontFamilies, mapBrandDefinedFonts } from '../utils/brand-elements.utils';
import { getActiveBrand } from '../store/selector/brand.selector';
import { Spinner } from 'react-bootstrap';
import {
    getAuthenticatedUserDetails,
    getAuthenticatedUserId,
    getUserCurrentlyWorkingBrandId,
} from '../store/selector/user.selector';
import { useLazyGetBrandOfUserQuery } from '../store/api/brand.api';
import { IBrand } from '../interfaces/brand.interface';
import { SetActiveBrand, SetUserBrands } from '../store/slice/brand.slice';
import { useUpdateCurrentlyWorkingBrandIdMutation } from '../store/api/user.api';
import { SetUserCurrentlyWorkingBrandId } from '../store/slice/user.slice';
import { useGetOrganizationTemplatesQuery } from '../store/api/template.api';
import { getOrganizationId } from '../store/selector/organization.selector';
import { useLazyGetUploadedAssetsQuery } from '../store/api/editor.api';
import { SetAssets, SetAssetsPage } from '../store/slice/assets.slice';
import { IUploadedAsset } from '../interfaces/assets.interface';
import {
    SetCampaignsLoading,
    SetFetchBrandElementsLoading,
    SetFetchInitialAssetsLoading,
    SetSessionsLoading,
} from '../store/slice/common.slice';
import {
    getDesignElementsColors,
    getDesignElementsFonts,
} from '../store/selector/brand-elements.selector';
import { SetDesignElements } from '../store/slice/brand-elements.slice';
import { displayErrorToast } from '../utils/ToastUtils';
import { useLazyGetBrandCampaignsQuery } from '../store/api/campaign.api';
import { SetCampaigns } from '../store/slice/campaign.slice';
import { SetTemplateTags } from '../store/slice/template.slice';
import { setTemplateDetailsInCampaign } from '../utils/campaign.utils';
import PrivateHeaderSidebarLayout from '../layouts/PrivateHeaderSidebarLayout';
import Dashboard from '../pages/Dashboard/Dashboard';
import CampaignPlans from '../pages/CampaignPlans/CampaignPlans';
import Editor from '../pages/Editor/Editor';
import Projects from '../pages/Projects/Projects';
import Campaign from '../pages/Campaign/Campaign';
import BrandProfile from '../pages/BrandProfile/BrandProfile';
import PrivateHeaderLayout from '../layouts/PrivateHeaderLayout';
import { useLazyGetActiveSessionQuery } from '../store/api/session.api';
import { SetCurrentlyWorkingSession, SetSessions } from '../store/slice/session.slice';
import Hootsuite from '../pages/Extras/Hootsuite';
import Metricool from '../pages/Extras/Metricool';
import SocialPilot from '../pages/Extras/SocialPilot';
import SproutSocial from '../pages/Extras/SproutSocial';
import { UserStage } from '../constants/user.constant';
import AdFromUrlDashboard from '../pages/Dashboard/AdFromUrlDashboard';
import config from '../config';

const PrivateRoutes = () => {
    const dispatch = useAppDispatch();
    const organizationId = useAppSelector(getOrganizationId);
    const resetStoreCounter = useAppSelector(getResetStoreCounter);
    const activeBrand = useAppSelector(getActiveBrand);
    const designElementsFonts = useAppSelector(getDesignElementsFonts);
    const designElementsColors = useAppSelector(getDesignElementsColors);
    const authenticatedUserId = useAppSelector(getAuthenticatedUserId);
    const userDetails = useAppSelector(getAuthenticatedUserDetails);
    const userCurrentlyWorkingBrandId = useAppSelector(getUserCurrentlyWorkingBrandId);
    const [fetchBrandData] = useLazyGetBrandElementsQuery();
    const [fetchBrandsOfUser] = useLazyGetBrandOfUserQuery();
    const [updateCurrentlyWorkingBrandId] = useUpdateCurrentlyWorkingBrandIdMutation();
    const { data: organizationTemplates } = useGetOrganizationTemplatesQuery(
        organizationId ? organizationId : 0,
        {
            skip: !organizationId,
        },
    );
    const [fetchCampaigns] = useLazyGetBrandCampaignsQuery();
    const [fetchUploadedAssets] = useLazyGetUploadedAssetsQuery();
    const [fetchActiveSessions] = useLazyGetActiveSessionQuery();
    const fetchSessionLoading = useAppSelector(getSessionsLoading);

    const [brandsLoading, setBrandsLoading] = useState(true);

    const editorStore = useMemo(() => {
        return createStore({
            key: process.env.REACT_APP_POLOTNO_API_KEY as string,
            showCredit: false,
        });
    }, [resetStoreCounter]);

    useEffect(() => {
        const fetchAllBrandInfo = async () => {
            if (authenticatedUserId) {
                setBrandsLoading(true);
                const brandsOfUserResponse = await fetchBrandsOfUser();
                const brandsOfUser: IBrand[] = brandsOfUserResponse.data;
                if (brandsOfUser && brandsOfUser.length > 0) {
                    dispatch(SetUserBrands(brandsOfUser));
                    let currentlyWorkingBrand: IBrand | null = null;
                    if (userCurrentlyWorkingBrandId) {
                        const searchBrand = brandsOfUser.find(
                            (brand: any) => brand.id === userCurrentlyWorkingBrandId,
                        );
                        if (searchBrand) {
                            currentlyWorkingBrand = searchBrand;
                        } else {
                            currentlyWorkingBrand = brandsOfUser[0];
                        }
                    } else {
                        currentlyWorkingBrand = brandsOfUser[0];
                    }
                    dispatch(SetActiveBrand(currentlyWorkingBrand));
                    if (
                        currentlyWorkingBrand &&
                        currentlyWorkingBrand.id !== userCurrentlyWorkingBrandId
                    ) {
                        dispatch(SetUserCurrentlyWorkingBrandId(currentlyWorkingBrand.id));
                        await updateCurrentlyWorkingBrandId({
                            brandId: currentlyWorkingBrand.id,
                        });
                    }
                    setBrandsLoading(false);
                }
            }
        };

        fetchAllBrandInfo();
    }, [authenticatedUserId]);

    useEffect(() => {
        const fetchBrandElementData = async () => {
            if (activeBrand) {
                dispatch(SetFetchBrandElementsLoading(true));
                try {
                    const response = await fetchBrandData(activeBrand.id);
                    if (response.data) {
                        const data: IGetBrandElements = response.data;
                        dispatch(
                            SetDesignElements({
                                fonts: data.fonts,
                                colors: data.colors,
                                logo: data.logo,
                            }),
                        );
                    }
                    dispatch(SetFetchBrandElementsLoading(false));
                } catch (e) {
                    displayErrorToast('Some error occured. Please try again later');
                }
            }
        };

        fetchBrandElementData();
    }, [activeBrand]);

    useEffect(() => {
        if (organizationTemplates) {
            let defaultTags: string[] = [];
            let customTags: string[] = [];
            organizationTemplates.forEach((template) => {
                if (template.organization_id === 0) {
                    defaultTags = [...defaultTags, ...template.tags];
                } else {
                    customTags = [...customTags, ...template.tags];
                }
            });
            dispatch(SetTemplateTags({ defaultTags, customTags }));
        }
    }, [organizationTemplates]);

    useEffect(() => {
        const fetchBrandCampaignData = async () => {
            if (!activeBrand) return;
            dispatch(SetCampaignsLoading(true));
            const response = await fetchCampaigns(activeBrand.id);
            const data = response.data;
            if (data && data.length > 0) {
                const updatedCampaigns = data.map((campaign) => {
                    return setTemplateDetailsInCampaign(
                        campaign,
                        organizationTemplates ? organizationTemplates : [],
                    );
                });

                const defaultCampaignsData = updatedCampaigns.filter(
                    (campaign) => campaign.brand_id === 0,
                );
                const brandCampaignsData = updatedCampaigns.filter(
                    (campaign) => campaign.brand_id !== 0,
                );

                dispatch(
                    SetCampaigns({
                        defaultCampaigns: defaultCampaignsData,
                        brandCampaigns: brandCampaignsData,
                    }),
                );
                dispatch(SetCampaignsLoading(false));
            }
        };

        fetchBrandCampaignData();
    }, [activeBrand, organizationTemplates, fetchCampaigns]);

    useEffect(() => {
        const fetchActiveSessionsData = async () => {
            if (!activeBrand) return;
            dispatch(SetSessionsLoading(true));
            const response = await fetchActiveSessions(activeBrand.id);
            const data = response.data;
            if (data) {
                dispatch(SetCurrentlyWorkingSession(data.currentlyWorkingSession));
                dispatch(SetSessions(data.sessions));
            }
            dispatch(SetSessionsLoading(false));
        };

        fetchActiveSessionsData();
    }, [activeBrand, fetchActiveSessions]);

    useEffect(() => {
        async function setFonts(designElementsFonts: IDesignElementsFonts) {
            const fontListURL = getGoogleFontsListAPI();
            fetch(fontListURL)
                .then((response) => response.json())
                .then((data) => {
                    const combinedArray = [
                        ...mapFontFamilies(BrandElementsFontsClass.HEADER, designElementsFonts),
                        ...mapFontFamilies(BrandElementsFontsClass.SUB_HEADER, designElementsFonts),
                        ...mapFontFamilies(BrandElementsFontsClass.ACCENT, designElementsFonts),
                        ...mapFontFamilies(BrandElementsFontsClass.BODY, designElementsFonts),
                    ];
                    setGoogleFonts([...combinedArray, ...data]);
                })
                .catch((error) => {
                    console.error('Error fetching font list:', error);
                });

            const brandDefinedFonts = [
                ...mapBrandDefinedFonts(BrandElementsFontsClass.HEADER, designElementsFonts),
                ...mapBrandDefinedFonts(BrandElementsFontsClass.SUB_HEADER, designElementsFonts),
                ...mapBrandDefinedFonts(BrandElementsFontsClass.ACCENT, designElementsFonts),
                ...mapBrandDefinedFonts(BrandElementsFontsClass.BODY, designElementsFonts),
            ];

            for (let i = 0; i < brandDefinedFonts.length; i++) {
                if (brandDefinedFonts[i].url) {
                    await editorStore.addFont({
                        fontFamily: brandDefinedFonts[i].fontFamily,
                        url: brandDefinedFonts[i].url,
                    });
                }
            }
        }

        if (designElementsFonts && editorStore) {
            setFonts(designElementsFonts);
        }
    }, [designElementsFonts, editorStore]);

    useEffect(() => {
        if (designElementsColors && editorStore) {
            designElementsColors.length > 1 &&
                setColorsPresetFunc(() => {
                    return designElementsColors;
                });
        }
    }, [designElementsColors, editorStore]);

    useEffect(() => {
        const fetchAssetsData = async () => {
            if (activeBrand) {
                dispatch(SetAssetsPage(1));
                dispatch(SetFetchInitialAssetsLoading(true));
                const response = await fetchUploadedAssets({
                    page: 1,
                    brandId: activeBrand.id,
                });
                const uploadedAssetsResponse = response.data;
                if (!uploadedAssetsResponse) {
                    dispatch(SetFetchInitialAssetsLoading(false));
                    return;
                }
                const uploadedAssets: IUploadedAsset[] = uploadedAssetsResponse.records.map(
                    (image) => ({
                        ...image,
                        isSelected: false,
                    }),
                );
                dispatch(
                    SetAssets({ assets: uploadedAssets, hasNext: uploadedAssetsResponse.hasNext }),
                );
                dispatch(SetFetchInitialAssetsLoading(false));
            }
        };

        fetchAssetsData();
    }, [activeBrand, fetchUploadedAssets]);

    return (
        <>
            {brandsLoading || fetchSessionLoading ? (
                <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-w-screen tw-h-screen">
                    <Spinner
                        as="span"
                        className="tw-mb-3 tw-w-12 tw-h-12 tw-bg-transparent tw-text-purple-200 "
                        role="status"
                        aria-hidden="true"
                        animation="border"
                    />
                    <h3>Loading your brand...</h3>
                </div>
            ) : (
                <Routes>
                    <Route
                        path="/"
                        element={
                            userDetails.stage === UserStage.NEW ? (
                                <PrivateHeaderLayout
                                    store={editorStore}
                                    children={<AdFromUrlDashboard />}
                                />
                            ) : (
                                <PrivateHeaderSidebarLayout
                                    store={editorStore}
                                    children={<Dashboard />}
                                />
                            )
                        }
                    />

                    <Route
                        path="/plans"
                        element={
                            <PrivateHeaderSidebarLayout
                                store={editorStore}
                                children={<CampaignPlans />}
                            />
                        }
                    />

                    <Route
                        path="/editor"
                        element={
                            <PrivateHeaderSidebarLayout
                                store={editorStore}
                                children={<Editor store={editorStore} />}
                            />
                        }
                    />

                    <Route
                        path="/projects"
                        element={
                            <PrivateHeaderSidebarLayout
                                store={editorStore}
                                children={<Projects store={editorStore} />}
                            />
                        }
                    />

                    <Route
                        path="/campaign"
                        element={
                            <PrivateHeaderSidebarLayout
                                store={editorStore}
                                children={<Campaign store={editorStore} />}
                            />
                        }
                    />

                    <Route
                        path="/brandProfile"
                        element={
                            <PrivateHeaderLayout store={editorStore} children={<BrandProfile />} />
                        }
                    />

                    <Route path="/start" element={<Navigate to="/brandProfile" />} />

                    <Route path="/hubspot/login" element={<Hubspot />} />
                    <Route path="/hootsuite/login" element={<Hootsuite />} />
                    <Route path="/metricool/login" element={<Metricool />} />
                    <Route path="/socialPilot/login" element={<SocialPilot />} />
                    <Route path="/sproutSocial/login" element={<SproutSocial />} />
                    <Route
                        path="/social"
                        element={
                            <PrivateHeaderLayout store={editorStore} children={<SocialsPage />} />
                        }
                    />

                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
            )}
        </>
    );
};

export default PrivateRoutes;
